"use client";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import React from "react";
import type { LyricsWithSlug } from "~/types";
import { CSS_VARIABLE_FONT_SIZE } from "./versuriFontSizeConstants";

interface VersuriLyricsProps {
  lyrics: LyricsWithSlug;
}

export default function VersuriLyrics({ lyrics }: VersuriLyricsProps) {
  const text = lyrics?.lyrics ?? "";
  const normalized = text.replace(/\r\n/g, "\n");

  const lines = normalized.split("\n");
  let refrenBlock: string | null = null;

  // Find the first [refren] block (case-insensitive) but don't keep marker lines
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].trim().toLowerCase() === "[refren]") {
      const blockLines: string[] = [];
      for (let j = i + 1; j < lines.length; j++) {
        if (lines[j].trim() === "") break;
        blockLines.push(lines[j]);
      }
      if (blockLines.length > 0 && refrenBlock === null) {
        refrenBlock = blockLines.join("\n");
      }
    }
  }

  // Remove all marker lines like [refren] (any case) so they don't appear in output
  const linesWithoutMarkerLines = lines.filter((l) => l.trim().toLowerCase() !== "[refren]");

  const lyricsWithoutMarker = linesWithoutMarkerLines.join("\n");

  if (!refrenBlock) {
    return (
      <LyricsTypography>
        {lyricsWithoutMarker}
      </LyricsTypography>
    );
  }

  const parts = lyricsWithoutMarker.split(refrenBlock).map((p) => p.trim());

  return (
    <Box>
      {parts.map((part, idx) => (
        <React.Fragment key={idx}>
          <LyricsTypography>
            {part}
          </LyricsTypography>

          {idx !== parts.length - 1 && (
            <LyricsTypography isChorus>
              {refrenBlock}
            </LyricsTypography>
          )}
        </React.Fragment>
      ))}
    </Box>
  );
}

function LyricsTypography({ children, isChorus }: { children: React.ReactNode; isChorus?: boolean }) {
  return (
    <Typography
      variant="body1"
      sx={{
        whiteSpace: "pre-line",
        fontSize: `var(${CSS_VARIABLE_FONT_SIZE})`,
        lineHeight: 1.6,
        fontWeight: isChorus ? 700 : 400,
        my: isChorus ? 3 : 0,
        textShadow: "0 1px 3px color-mix(in srgb, var(--mui-palette-background-default), var(--mui-palette-background-default) 70%)",
      }}
    >
      {children}
    </Typography>
  );
}